/**
 * PM portal · one standards workflow: whitelisted project info (NO pricing; labor identity only
 * post-release), criteria editor + library drawer (editable in pm_drafting/returned), submit,
 * Q&A thread, meeting-notes form, annexure viewer with 确认 / 要求修改.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMe, pmWorkflowDetail, pmStandardsLibrary, pmWorkflowMessages, pmWorkflowActions,
  pmWorkflowDocs, pmUploadWorkflowDoc, pmDeleteWorkflowDoc, pmWorkflowDocUrl,
  annexureDownloadUrl, WF_STATUS,
  type MeResponse, type ApiError, type AcceptanceCriterion, type LibraryTradeView,
} from '../lib/api';
import WorkflowTimeline from '../components/WorkflowTimeline';
import MdView from '../components/MdView';
import { MeetingForm, AnnexureBadges } from '../components/StandardsWorkflowCard';
import { Skeleton } from '../components/ui/Skeleton';

function errText(e: unknown): string {
  const b = (e as ApiError)?.body;
  return b?.detail ?? b?.error ?? (e as Error)?.message ?? '失败 / failed';
}

export default function PmStandardsDetail() {
  const { wid } = useParams<{ wid: string }>();
  const qc = useQueryClient();
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const { data, isLoading, isError } = useQuery({ queryKey: ['pm-workflow', wid], queryFn: () => pmWorkflowDetail(wid!), enabled: !!wid && me?.account?.role === 'site_pm' });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true); setErr(null);
    try { await fn(); await qc.invalidateQueries({ queryKey: ['pm-workflow', wid] }); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  }

  if (!me?.authenticated) return <div className="card">请先登录 / please log in.</div>;
  if (me.account?.role !== 'site_pm') return <div className="card">此页仅项目经理可见 / site-PM only.</div>;
  if (isLoading) return <div className="card"><Skeleton lines={4} /></div>;
  if (isError || !data) return <div className="card">未找到 / not found. <Link to="/pm/standards">← 返回</Link></div>;

  const wf = data.workflow;
  const editable = wf.criteria_editable;

  return (
    <div>
      <div className="small"><Link to="/pm/standards">← 我的验收标准 / My standards</Link></div>
      <h2 style={{ margin: '8px 0 2px' }}>{data.package.project_name ?? data.package.name}</h2>
      <div className="row-wrap" style={{ alignItems: 'center', marginBottom: 8 }}>
        <span className={`pill small ${WF_STATUS[wf.status]?.pill}`}>{WF_STATUS[wf.status]?.label}</span>
        {data.labor_co && <span className="pill pill-muted small">劳务单位：{data.labor_co.name}</span>}
        {data.package.trades.length > 0 && <span className="muted small">专业：{data.package.trades.join('、')}</span>}
      </div>
      <WorkflowTimeline status={wf.status} events={data.events} />
      {err && <div className="card small" style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }}>{err}</div>}

      {wf.latest_return_comment && (wf.status === 'returned') && (
        <div className="card" style={{ borderColor: 'var(--warn)', marginBottom: 10 }}>
          <strong>估价退回意见：</strong><MdView md={wf.latest_return_comment} className="small" />
        </div>
      )}

      {data.package.scope_summary_md && (
        <div className="card" style={{ marginBottom: 10 }}>
          <h4 style={{ marginTop: 0 }}>项目范围 / Scope</h4>
          <MdView md={data.package.scope_summary_md} className="small" />
        </div>
      )}

      <CriteriaEditor wid={wf.id} criteria={data.criteria} editable={editable} busy={busy} act={act} />

      {editable && (
        <div className="row-wrap" style={{ margin: '10px 0' }}>
          <button className="btn-primary" disabled={busy || data.criteria.length === 0} onClick={() => act(() => pmWorkflowActions.submit(wf.id))}>
            ✓ 提交估价审核 / Submit for review
          </button>
          {data.criteria.length === 0 && <span className="muted small">请先选定至少一条验收标准。/ pick at least one standard first.</span>}
        </div>
      )}

      <StandardsDocsCard wid={wf.id} frozen={wf.status === 'finalized'} />

      {data.labor_co && <QaThread wid={wf.id} status={wf.status} busy={busy} act={act} />}

      {(wf.status === 'labor_clear' || wf.status === 'revision_requested') && (
        <div className="card" style={{ marginBottom: 10 }}>
          <MeetingForm busy={busy} initialNotes={wf.meeting_notes_md} onSubmit={(b) => act(() => pmWorkflowActions.meeting(wf.id, b))} />
        </div>
      )}

      {data.annexure && (
        <div className="card" style={{ marginBottom: 10 }}>
          <div className="row-wrap" style={{ alignItems: 'center', marginBottom: 6 }}>
            <h4 style={{ margin: 0 }}>验收标准确认书（第 {data.annexure.version} 版）</h4>
            <AnnexureBadges v={data.annexure} />
            <span className="spacer" />
            <a href={annexureDownloadUrl(data.annexure.id)}><button>⬇ 下载 Word</button></a>
          </div>
          <MdView md={data.annexure.content_md} className="small" />
          {wf.status === 'annexure_review' && !data.annexure.pm_confirmed_at && (
            <ConfirmOrRevise busy={busy}
              onConfirm={() => act(() => pmWorkflowActions.confirmAnnexure(wf.id))}
              onRevise={(c) => act(() => pmWorkflowActions.requestRevision(wf.id, c))} />
          )}
          {wf.status === 'annexure_review' && data.annexure.pm_confirmed_at && !data.annexure.labor_confirmed_at && (
            <div className="muted small" style={{ marginTop: 6 }}>您已确认 — 等待劳务单位确认。/ You confirmed — waiting for the labor company.</div>
          )}
          {wf.status === 'finalized' && <div className="small" style={{ color: 'var(--good)', marginTop: 6 }}>✓ 双方已确认定稿。/ Finalized by both parties.</div>}
        </div>
      )}
    </div>
  );
}

function ConfirmOrRevise({ busy, onConfirm, onRevise }: { busy: boolean; onConfirm: () => void; onRevise: (comment: string) => void }) {
  const [revising, setRevising] = useState(false);
  const [comment, setComment] = useState('');
  return (
    <div style={{ marginTop: 8 }}>
      {!revising ? (
        <div className="row-wrap">
          <button className="btn-primary" disabled={busy} onClick={onConfirm}>✓ 确认验收标准 / Confirm</button>
          <button disabled={busy} onClick={() => setRevising(true)}>✎ 要求修改 / Request revision</button>
        </div>
      ) : (
        <div className="row-wrap" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: 1, minWidth: 240, marginBottom: 0 }}>
            <label>修改意见（必填）/ comment (required)</label>
            <input value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
          <button className="btn-primary" disabled={busy || !comment.trim()} onClick={() => { onRevise(comment); setRevising(false); }}>提交 / Submit</button>
          <button disabled={busy} onClick={() => setRevising(false)}>×</button>
        </div>
      )}
    </div>
  );
}

/** 标准文件 — the PM attaches the project's own standards documents (过程/竣工验收标准) to the
 *  workflow. Released to the labor portal together with the criteria; frozen once finalized. */
function StandardsDocsCard({ wid, frozen }: { wid: string; frozen: boolean }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['pm-workflow-docs', wid], queryFn: () => pmWorkflowDocs(wid) });
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState('过程验收标准');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const docs = data?.docs ?? [];

  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setErr(null);
    try { await fn(); await qc.invalidateQueries({ queryKey: ['pm-workflow-docs', wid] }); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <h4 style={{ marginTop: 0 }}>标准文件 / Standards documents（{docs.length}）</h4>
      <p className="muted small">项目自己的验收标准文件（如《过程验收标准》《竣工验收标准》）可上传在这里，发出后劳务方会在门户中看到。/ Attach the project's own standards documents; the labor company sees them once released.</p>
      {err && <div className="small" style={{ color: 'var(--bad)', marginBottom: 6 }}>{err}</div>}
      {docs.length > 0 && (
        <ul className="small" style={{ margin: '0 0 8px', paddingLeft: 18 }}>
          {docs.map((d) => (
            <li key={d.id} style={{ marginBottom: 2 }}>
              <a href={pmWorkflowDocUrl(wid, d.id)}>{d.file_name}</a>
              {d.category && <span className="pill pill-muted small" style={{ marginLeft: 6 }}>{d.category}</span>}
              {d.uploaded_by && <span className="muted"> · {d.uploaded_by}</span>}
              {!frozen && (
                <button className="small" style={{ marginLeft: 8 }} disabled={busy}
                  onClick={() => run(() => pmDeleteWorkflowDoc(wid, d.id))}>删除 / delete</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {docs.length === 0 && <div className="muted small" style={{ marginBottom: 8 }}>暂无文件 / no documents yet.</div>}
      {!frozen && (
        <div className="row-wrap" style={{ alignItems: 'flex-end', gap: 8 }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>类型 / type</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="过程验收标准">过程验收标准</option>
              <option value="竣工验收标准">竣工验收标准</option>
              <option value="其他资料">其他资料 / other</option>
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>文件 / file</label>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </div>
          <button className="btn-primary" disabled={!file || busy}
            onClick={() => run(async () => { await pmUploadWorkflowDoc(wid, file!, category); setFile(null); })}>
            ⬆ 上传 / Upload
          </button>
        </div>
      )}
      {frozen && <div className="muted small">确认书已定稿，文件已锁定。/ Finalized — documents are locked.</div>}
    </div>
  );
}

function CriteriaEditor({ wid, criteria, editable, busy, act }: {
  wid: string; criteria: AcceptanceCriterion[]; editable: boolean; busy: boolean; act: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [showLibrary, setShowLibrary] = useState(false);
  const [adding, setAdding] = useState(false);
  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div className="row-wrap" style={{ alignItems: 'center', marginBottom: 6 }}>
        <h4 style={{ margin: 0 }}>验收标准（{criteria.length} 条）{!editable && <span className="pill pill-muted small" style={{ marginLeft: 6 }}>已锁定 / locked</span>}</h4>
        <span className="spacer" />
        {editable && <button onClick={() => setShowLibrary(!showLibrary)}>📚 标准库 / Library</button>}
        {editable && <button onClick={() => setAdding(!adding)}>＋ 自定义 / Add custom</button>}
      </div>
      {showLibrary && <LibraryDrawer wid={wid} busy={busy} act={act} onClose={() => setShowLibrary(false)} />}
      {adding && <AddCriterionForm wid={wid} busy={busy} act={act} onDone={() => setAdding(false)} />}
      {criteria.length === 0 && <div className="muted small">从标准库预填或手动添加。/ Seed from the library or add manually.</div>}
      {criteria.map((c) => <CriterionRow key={c.id} c={c} editable={editable} busy={busy} act={act} />)}
    </div>
  );
}

function CriterionRow({ c, editable, busy, act }: { c: AcceptanceCriterion; editable: boolean; busy: boolean; act: (fn: () => Promise<unknown>) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(c.label_zh ?? '');
  const [rule, setRule] = useState(c.pass_rule_md ?? '');
  const [critical, setCritical] = useState(c.is_critical);
  if (!editing) {
    return (
      <div style={{ borderTop: '1px solid var(--line, #e5e0d5)', padding: '6px 0' }}>
        <div className="row-wrap" style={{ alignItems: 'center' }}>
          {c.trade && <span className="pill pill-muted small">{c.trade}</span>}
          <strong className="small">{c.label_zh ?? c.label_en ?? '—'}</strong>
          {c.is_critical && <span className="pill pill-warn small">关键 ⭐</span>}
          <span className="spacer" />
          {editable && <button className="small" disabled={busy} onClick={() => setEditing(true)}>✎</button>}
          {editable && <button className="small" disabled={busy} style={{ color: 'var(--bad)' }} onClick={() => { if (confirm('删除此条标准？/ delete?')) act(() => pmWorkflowActions.deleteCriterion(c.id)); }}>🗑</button>}
        </div>
        <MdView md={c.pass_rule_md} className="small muted" />
      </div>
    );
  }
  return (
    <div style={{ borderTop: '1px solid var(--line, #e5e0d5)', padding: '6px 0' }}>
      <div className="field"><label>验收项目 / label</label><input value={label} onChange={(e) => setLabel(e.target.value)} /></div>
      <div className="field"><label>合格标准 / pass rule</label><textarea rows={3} value={rule} onChange={(e) => setRule(e.target.value)} /></div>
      <label className="small row" style={{ gap: 6, alignItems: 'center', marginBottom: 6 }}>
        <input type="checkbox" checked={critical} onChange={(e) => setCritical(e.target.checked)} /> 关键项 / critical
      </label>
      <div className="row-wrap">
        <button className="btn-primary" disabled={busy} onClick={async () => { await act(() => pmWorkflowActions.patchCriterion(c.id, { label_zh: label, pass_rule_md: rule, is_critical: critical })); setEditing(false); }}>保存 / Save</button>
        <button disabled={busy} onClick={() => setEditing(false)}>取消 / Cancel</button>
      </div>
    </div>
  );
}

function AddCriterionForm({ wid, busy, act, onDone }: { wid: string; busy: boolean; act: (fn: () => Promise<unknown>) => Promise<void>; onDone: () => void }) {
  const [trade, setTrade] = useState('');
  const [label, setLabel] = useState('');
  const [rule, setRule] = useState('');
  const [critical, setCritical] = useState(false);
  return (
    <div style={{ border: '1px dashed var(--line, #cbc4b3)', borderRadius: 8, padding: 8, marginBottom: 8 }}>
      <div className="row-wrap">
        <div className="field" style={{ marginBottom: 6 }}><label>专业 / trade</label><input value={trade} onChange={(e) => setTrade(e.target.value)} placeholder="吊顶" /></div>
        <div className="field" style={{ flex: 1, minWidth: 160, marginBottom: 6 }}><label>验收项目 / label</label><input value={label} onChange={(e) => setLabel(e.target.value)} /></div>
      </div>
      <div className="field" style={{ marginBottom: 6 }}><label>合格标准 / pass rule</label><textarea rows={2} value={rule} onChange={(e) => setRule(e.target.value)} /></div>
      <label className="small row" style={{ gap: 6, alignItems: 'center', marginBottom: 6 }}>
        <input type="checkbox" checked={critical} onChange={(e) => setCritical(e.target.checked)} /> 关键项 / critical
      </label>
      <div className="row-wrap">
        <button className="btn-primary" disabled={busy || !label.trim()} onClick={async () => { await act(() => pmWorkflowActions.addCriterion(wid, { trade: trade || undefined, label_zh: label, pass_rule_md: rule, is_critical: critical })); onDone(); }}>添加 / Add</button>
        <button disabled={busy} onClick={onDone}>取消 / Cancel</button>
      </div>
    </div>
  );
}

function LibraryDrawer({ wid, busy, act, onClose }: { wid: string; busy: boolean; act: (fn: () => Promise<unknown>) => Promise<void>; onClose: () => void }) {
  const { data } = useQuery({ queryKey: ['pm-standards-library'], queryFn: pmStandardsLibrary });
  const [openTrade, setOpenTrade] = useState<string | null>(null);
  return (
    <div style={{ border: '1px solid var(--line, #cbc4b3)', borderRadius: 8, padding: 8, marginBottom: 8, background: 'var(--paper-soft, #faf7f0)' }}>
      <div className="row-wrap" style={{ alignItems: 'center', marginBottom: 6 }}>
        <strong className="small">标准库（精装修 8 大专业）/ Standards library</strong>
        <span className="spacer" />
        <button className="small" disabled={busy} title="按清单专业整包预填（已有标准时不重复）" onClick={() => act(() => pmWorkflowActions.seedFromLibrary(wid))}>⚡ 按清单预填 / Seed from BOQ</button>
        <button className="small" onClick={onClose}>×</button>
      </div>
      {(data?.trades ?? []).map((t) => (
        <LibraryTradeBlock key={t.trade_code} t={t} open={openTrade === t.trade_code} onToggle={() => setOpenTrade(openTrade === t.trade_code ? null : t.trade_code)} wid={wid} busy={busy} act={act} />
      ))}
    </div>
  );
}

function LibraryTradeBlock({ t, open, onToggle, wid, busy, act }: {
  t: LibraryTradeView; open: boolean; onToggle: () => void; wid: string; busy: boolean; act: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  return (
    <div style={{ borderTop: '1px solid var(--line, #e5e0d5)' }}>
      <div className="row-wrap small" style={{ alignItems: 'center', padding: '4px 0', cursor: 'pointer' }} onClick={onToggle}>
        <strong>{t.trade_zh}</strong><span className="muted">{t.trade_en} · {t.standards.length} 条</span>
        <span className="spacer" /><span>{open ? '▾' : '▸'}</span>
      </div>
      {open && t.standards.map((s, i) => (
        <div key={i} className="row-wrap small" style={{ alignItems: 'flex-start', padding: '2px 0 2px 12px', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <strong>{s.label_zh}</strong>{s.is_critical && ' ⭐'}
            <div className="muted">{s.pass_rule_md}</div>
          </div>
          <button className="small" disabled={busy} onClick={() => act(() => pmWorkflowActions.addCriterion(wid, { trade: t.trade_zh, label_zh: s.label_zh, label_en: s.label_en, pass_rule_md: s.pass_rule_md, is_critical: s.is_critical }))}>＋ 选用</button>
        </div>
      ))}
    </div>
  );
}

function QaThread({ wid, status, busy, act }: { wid: string; status: string; busy: boolean; act: (fn: () => Promise<unknown>) => Promise<void> }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['pm-workflow-messages', wid], queryFn: () => pmWorkflowMessages(wid), refetchInterval: 30_000 });
  const [reply, setReply] = useState('');
  const messages = data?.messages ?? [];
  const canReply = ['released', 'labor_clear', 'annexure_review', 'revision_requested'].includes(status);
  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <h4 style={{ marginTop: 0 }}>劳务答疑 / Labor Q&A（{messages.length}）</h4>
      {messages.length === 0 && <div className="muted small">劳务方的问题会出现在这里。/ The labor company's questions land here.</div>}
      {messages.map((m) => (
        <div key={m.id} className="small" style={{ padding: '4px 0', borderTop: '1px solid var(--line, #efe9dc)' }}>
          <span className={`pill small ${m.direction === 'inbound' ? 'pill-warn' : 'pill-stage'}`}>{m.direction === 'inbound' ? '劳务' : '我方'}</span>{' '}
          <span className="muted">{m.author_name ?? ''} · {m.created_at.slice(0, 16).replace('T', ' ')}</span>
          <div style={{ whiteSpace: 'pre-wrap' }}>{m.body_md}</div>
        </div>
      ))}
      {canReply && (
        <div className="row-wrap" style={{ alignItems: 'flex-end', marginTop: 6 }}>
          <div className="field" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
            <label>回复 / reply</label>
            <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="回答劳务方的问题…" />
          </div>
          <button className="btn-primary" disabled={busy || !reply.trim()} onClick={async () => {
            await act(() => pmWorkflowActions.sendMessage(wid, reply));
            setReply('');
            await qc.invalidateQueries({ queryKey: ['pm-workflow-messages', wid] });
          }}>发送 / Send</button>
        </div>
      )}
    </div>
  );
}
